import React, { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import "./OfferCard.css";
import Button from "Components/Other Components/button";
import { useNotification } from "Contexts/NotificationContext";
import { useTranslation } from "react-i18next";

const OfferCard = ({
  title,
  price,
  paymentType,
  color,
  props = {},
  selected,
  onClick,
  pausedInfo = {
    message: "This package is currently active.",
    expires: "31.10.2025",
    expiring: false,
  },
  btnText,
  btnDisabled,
  index = 0,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const cardRef = useRef(null);
  const { showNotification: showNotificationContext } = useNotification();
  const { t } = useTranslation("home");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Handle notification animation when card becomes selected
  useEffect(() => {
    if (selected && isVisible) {
      // Small delay to let the card animation finish first
      const timer = setTimeout(() => {
        setShowNotification(true);
      }, 300 + index * 200 + 100); // Card animation duration + delay + small buffer

      return () => clearTimeout(timer);
    } else {
      setShowNotification(false);
    }
  }, [selected, isVisible, index]);

  const renderPropertyValue = (value) => {
    if (typeof value === "boolean") {
      return (
        <div className={`property-indicator ${value ? "enabled" : "disabled"}`}>
          <div className="indicator-circle"></div>
        </div>
      );
    }
    return <span className="property-value">{value}</span>;
  };

  return (
    <motion.div
      ref={cardRef}
      style={{ "--card-shadow-color": color }}
      className={`offer-card-container ${selected ? "selected-container" : ""}`}
      initial={{
        opacity: 0,
        y: 50,
        transition: { duration: 0.1, ease: "easeOut", delay: 0 },
      }}
      animate={
        isVisible
          ? {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.3,
                delay: index * 0.2,
                ease: "easeOut",
              },
            }
          : {
              opacity: 0,
              y: 50,
              transition: {
                duration: 0.3,
                delay: index * 0.2,
                ease: "easeOut",
              },
            }
      }
      whileHover={
        selected
          ? {}
          : {
              filter: `drop-shadow(0px 0px calc(var(--SHADOWBLUR) + 5px) var(--card-shadow-color, var(--SHADOWCOLOR)))`,
              transition: { duration: 0.1, ease: "easeOut", delay: 0 },
            }
      }
    >
      <div className={"offer-card " + (selected ? "selected" : "")}>
        <div className="card-header">
          <h3 className="card-title">{title}</h3>
        </div>
        <div className="card-price-container">
          <h3 className="card-price" style={{ color: color }}>
            {price}
          </h3>
          <p className="card-price-payment-plan">{paymentType}</p>
        </div>
        <div className="card-content">
          <div className="properties-list">
            {Object.entries(props).map(([key, value]) => (
              <div key={key} className="property-item">
                <span className="property-name">{key}</span>
                {renderPropertyValue(value)}
              </div>
            ))}
          </div>
        </div>
        <div className="card-footer">
          <Button
            onClick={onClick}
            btnBehaviour={"btn-fill"}
            btnStyle={selected || btnDisabled ? "btn-default" : "btn-positive"}
          >
            {btnText}
          </Button>
        </div>
      </div>
      <motion.div
        className="offer-card-notification"
        initial={{ height: 0, opacity: 0 }}
        animate={
          showNotification
            ? { height: "auto", opacity: 1 }
            : { height: 0, opacity: 0 }
        }
        transition={{
          duration: 0.4,
          ease: "easeOut",
        }}
      >
        {/* Notification content can go here */}
        <div className="offer-card-notification-content">
          <span className="offer-card-info-message">{pausedInfo.message}</span>
          <span
            className={
              "offer-card-info-expires " + (pausedInfo.expiring ? "paused" : "")
            }
          >
            {pausedInfo.expires}
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default OfferCard;
